import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { NavSettings, SitePages } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import MediaUpload from "@/components/MediaUpload";
import { Eye, EyeOff, GripVertical, ArrowUpRight, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import NavPreview from "@/components/NavPreview";
import { notifyPreviewRefresh } from "@/components/admin/GlobalPreviewPane";

const BUILTIN_LABELS = {
  home: "Home",
  equipment: "Equipment",
  services: "Services",
  contact: "Contact",
};

const BUILTIN_HINTS = {
  home: "Logo always links home — this controls the 'Home' text link",
  equipment: "Links to the Equipment catalog page (/equipment) — hides it from the header if off",
  services: "Anchor link to the services section on the homepage",
  contact: "Anchor link to the contact section on the homepage",
};

const SortableRow = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-3 px-6 py-5 border-b border-white/5 last:border-b-0 bg-slate-950/40">
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="touch-none cursor-grab active:cursor-grabbing text-slate-500 hover:text-white p-1"
        aria-label="Drag to reorder"
        data-testid={`drag-${id}`}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      {children}
    </div>
  );
};

const NavigationManager = () => {
  const [settings, setSettings] = useState(null);
  const [pages, setPages] = useState([]);
  const [items, setItems] = useState([]); // ordered list of { id, kind, ... }
  const [busy, setBusy] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const recompose = (order, navSettings, navPages) => {
    // Build a map of all known items
    const all = {};
    Object.keys(BUILTIN_LABELS).forEach((k) => {
      all[k] = {
        id: k,
        kind: "builtin",
        key: k,
        label: BUILTIN_LABELS[k],
        hint: BUILTIN_HINTS[k],
        visible: !!navSettings[`show_${k}`],
      };
    });
    navPages.forEach((p) => {
      const id = `page:${p.id}`;
      all[id] = {
        id,
        kind: "page",
        page: p,
        label: p.title,
        hint: `/p/${p.slug}`,
        visible: !!p.show_in_nav,
      };
    });
    const seen = new Set();
    const ordered = [];
    (order || []).forEach((id) => {
      if (all[id] && !seen.has(id)) {
        ordered.push(all[id]);
        seen.add(id);
      }
    });
    // Append items missing from saved order (newly added pages, new built-ins)
    Object.values(all).forEach((it) => {
      if (!seen.has(it.id)) ordered.push(it);
    });
    return ordered;
  };

  const load = async () => {
    try {
      const [navSettings, navPages] = await Promise.all([
        NavSettings.get(),
        SitePages.list(true),
      ]);
      setSettings(navSettings);
      setPages(navPages);
      setItems(recompose(navSettings.order, navSettings, navPages));
    } catch {
      toast.error("Couldn't load navigation settings");
    }
  };
  useEffect(() => { load(); }, []);

  const persistOrder = async (newItems) => {
    const order = newItems.map((it) => it.id);
    setBusy(true);
    try {
      await NavSettings.update({ order });
      notifyPreviewRefresh();
    } catch {
      toast.error("Couldn't save order");
      load();
    } finally {
      setBusy(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((curr) => {
      const oldIdx = curr.findIndex((i) => i.id === active.id);
      const newIdx = curr.findIndex((i) => i.id === over.id);
      const next = arrayMove(curr, oldIdx, newIdx);
      persistOrder(next);
      return next;
    });
  };

  const toggleBuiltin = async (key, value) => {
    const flag = `show_${key}`;
    setSettings((s) => ({ ...s, [flag]: value }));
    setItems((curr) => curr.map((i) => (i.id === key ? { ...i, visible: value } : i)));
    try {
      await NavSettings.update({ [flag]: value });
      toast.success(`${BUILTIN_LABELS[key]} — ${value ? "shown" : "hidden"}`);
      notifyPreviewRefresh();
    } catch {
      toast.error("Save failed");
      load();
    }
  };

  const toggleQuoteButton = async (value) => {
    setSettings((s) => ({ ...s, show_quote_button: value }));
    try {
      await NavSettings.update({ show_quote_button: value });
      toast.success(`Quote button — ${value ? "shown" : "hidden"}`);
      notifyPreviewRefresh();
    } catch {
      toast.error("Save failed");
      load();
    }
  };

  const togglePage = async (page, value) => {
    try {
      await SitePages.update(page.id, { show_in_nav: value });
      setPages((ps) => ps.map((x) => (x.id === page.id ? { ...x, show_in_nav: value } : x)));
      setItems((curr) => curr.map((i) => (i.id === `page:${page.id}` ? { ...i, visible: value, page: { ...i.page, show_in_nav: value } } : i)));
      toast.success(`${page.title} — ${value ? "shown in header" : "removed from header"}`);
      notifyPreviewRefresh();
    } catch {
      toast.error("Save failed");
    }
  };

  if (!settings) {
    return <div className="p-10 text-slate-400">Loading…</div>;
  }

  return (
    <div className="p-10 space-y-8" data-testid="navigation-manager">
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-2">Header</div>
          <h1 className="font-display font-black text-4xl text-white">Navigation</h1>
          <p className="text-slate-400 text-sm mt-2">
            Drag <GripVertical className="inline h-3.5 w-3.5 align-text-bottom" /> to reorder. Toggle to show/hide. Hidden pages remain accessible by direct URL.
          </p>
        </div>
        {busy && <Loader2 className="h-4 w-4 animate-spin text-atc-blue" />}
      </div>

      <div className="sticky top-0 z-20 -mx-2 px-2 py-3 bg-slate-900/70 backdrop-blur-xl rounded-2xl">
        <NavPreview items={items} showQuoteButton={settings.show_quote_button} />
      </div>

      <section className="glass rounded-2xl overflow-hidden">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            {items.map((it) => (
              <SortableRow key={it.id} id={it.id}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {it.visible ? (
                    <Eye className="h-4 w-4 text-atc-green shrink-0" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-slate-500 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="text-white font-medium text-sm truncate">
                      {it.label}
                      {it.kind === "page" && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-atc-blue bg-atc-blue/10 px-1.5 py-0.5 rounded">Custom</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{it.hint}</div>
                  </div>
                </div>
                <Switch
                  checked={it.visible}
                  onCheckedChange={(v) =>
                    it.kind === "builtin" ? toggleBuiltin(it.key, v) : togglePage(it.page, v)
                  }
                  data-testid={`switch-${it.id}`}
                />
              </SortableRow>
            ))}
          </SortableContext>
        </DndContext>
      </section>

      <section className="glass rounded-2xl flex items-center justify-between gap-6 px-6 py-5">
        <div className="flex items-center gap-3">
          {settings.show_quote_button ? (
            <Eye className="h-4 w-4 text-atc-green shrink-0" />
          ) : (
            <EyeOff className="h-4 w-4 text-slate-500 shrink-0" />
          )}
          <div>
            <div className="text-white font-medium text-sm">Request Quote button</div>
            <div className="text-xs text-slate-500 mt-0.5">The gradient call-to-action at the right of the header (always last; not reorderable)</div>
          </div>
        </div>
        <Switch
          checked={settings.show_quote_button}
          onCheckedChange={toggleQuoteButton}
          data-testid="switch-show_quote_button"
        />
      </section>

      <PoweredBySection settings={settings} setSettings={setSettings} />

      <div className="text-xs text-slate-500 flex items-center gap-2">
        Need to add a new custom page?
        <Link to="/admin/site-pages" className="text-atc-blue inline-flex items-center gap-1 hover:gap-2 transition-all">
          Manage Site Pages <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
};

export default NavigationManager;

const PoweredBySection = ({ settings, setSettings }) => {
  const [text, setText] = useState(settings.powered_by_text || "");
  const [logo, setLogo] = useState(settings.powered_by_logo_url || "");
  const [blend, setBlend] = useState(settings.powered_by_blend || "none");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setText(settings.powered_by_text || "");
    setLogo(settings.powered_by_logo_url || "");
    setBlend(settings.powered_by_blend || "none");
  }, [settings.powered_by_text, settings.powered_by_logo_url, settings.powered_by_blend]);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await NavSettings.update({
        powered_by_text: text,
        powered_by_logo_url: logo,
        powered_by_blend: blend,
      });
      setSettings((s) => ({ ...s, ...updated }));
      toast.success("Header updated");
      notifyPreviewRefresh();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const dirty =
    text !== (settings.powered_by_text || "") ||
    logo !== (settings.powered_by_logo_url || "") ||
    blend !== (settings.powered_by_blend || "none");

  return (
    <section className="glass rounded-2xl px-6 py-5 space-y-4" data-testid="powered-by-section">
      <div>
        <div className="text-white font-medium text-sm">Header — Powered by</div>
        <div className="text-xs text-slate-500 mt-0.5">
          Optional text + logo that appears under &ldquo;ATC Mold and Water&rdquo; in the header. Logo is rendered up to ~28px tall (max 180px wide) so it never breaks the layout.
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-1.5 block">Text label</label>
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Powered by:"
            className="bg-slate-950 border-white/10"
            data-testid="powered-by-text-input"
          />
          <div className="text-[10px] text-slate-500 mt-1.5">Leave blank to hide the label and show only the logo.</div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-1.5 block">Logo image</label>
          <MediaUpload
            value={logo}
            onChange={setLogo}
            kind="image"
            testId="powered-by-logo-upload"
            label="Partner / Sponsor logo"
          />
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-1.5 block">Background blend</label>
        <select
          value={blend}
          onChange={(e) => setBlend(e.target.value)}
          className="w-full sm:w-72 px-3 py-2 rounded-lg bg-slate-950 border border-white/10 text-sm text-white"
          data-testid="powered-by-blend-select"
        >
          <option value="none">None — keep image as-is</option>
          <option value="multiply">Hide white/light background (multiply)</option>
          <option value="screen">Hide black/dark background (screen)</option>
          <option value="luminosity">Match header tone (luminosity)</option>
        </select>
        <div className="text-[10px] text-slate-500 mt-1.5">
          Pick <em>Hide white background</em> if your logo is dark art on a white/light background (most common).
          Pick <em>Hide black background</em> if it&apos;s the opposite. If your PNG already has transparency, choose <em>None</em>.
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        {dirty && <span className="text-xs text-amber-300">Unsaved changes</span>}
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full brand-gradient text-white text-xs font-semibold hover:opacity-95 disabled:opacity-50"
          data-testid="powered-by-save"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save
        </button>
      </div>
    </section>
  );
};
